import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Plus } from 'lucide-react';
import type { AgentProfileView } from '@autopilot/contracts';
import { api } from '../../api/client';
import { qk } from '../../api/hooks';
import { ApiError } from '../../api/errors';
import { EmptyState, ErrorAlert, QueryBoundary } from '../../components/States';
import { ToneBadge } from '../../components/StatusBadge';
import { SYNC_STATE_LABEL } from '../../lib/labels';
import { GesellschaftSelect, useGesellschaftNames } from './GesellschaftSelect';

export function AgentsTab() {
  const qc = useQueryClient();
  const names = useGesellschaftNames();
  const [edit, setEdit] = useState<AgentProfileView | 'new' | null>(null);
  const q = useQuery({
    queryKey: qk.agents,
    queryFn: api.listAgents,
    refetchInterval: (query) => ((query.state.data ?? []).some((a) => a.syncState === 'PENDING') ? 2000 : false),
  });
  const toggle = useMutation({
    mutationFn: (a: AgentProfileView) => api.updateAgent(a.id, { expectedRowVersion: a.rowVersion, active: !a.active }),
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.agents }),
  });
  const items = q.data ?? [];
  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 820, flex: 1 }}>
          Bearbeiterprofile erscheinen in Nidus als Absender der Antworten. Pro Ticket wird zufällig eines der für die Gesellschaft erlaubten, aktiven Profile gewählt. Laufende und historische Operationen behalten ihren damaligen Namen.
        </Typography>
        <Button variant="contained" startIcon={<Plus size={18} />} onClick={() => setEdit('new')}>
          Profil anlegen
        </Button>
      </Box>
      <ErrorAlert error={toggle.error} action="Profil aktivieren/deaktivieren" onClose={() => toggle.reset()} />
      <Card sx={{ mt: 1 }}>
        <QueryBoundary query={q} action="Bearbeiterprofile laden" loadingRows={4}>
          {items.length === 0 ? (
            <EmptyState title="Noch keine Bearbeiterprofile" description="Ohne aktives Profil werden Tickets einer Gesellschaft übersprungen." />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table aria-label="Bearbeiterprofile" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Anzeigename</TableCell>
                    <TableCell>Gesellschaften</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Nidus-Abgleich</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((a) => {
                    const s = SYNC_STATE_LABEL[a.syncState];
                    return (
                      <TableRow key={a.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {a.displayName}
                          </Typography>
                          {a.signature && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Signatur: {a.signature}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>{names(a.gesellschaftIds)}</TableCell>
                        <TableCell>{a.active ? <ToneBadge tone="success" label="Aktiv" /> : <ToneBadge tone="neutral" label="Deaktiviert" />}</TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <ToneBadge tone={s.tone} label={s.label} spinning={a.syncState === 'PENDING'} />
                          {a.syncError && (
                            <Typography variant="caption" component="div" sx={{ color: 'error.dark', mt: 0.5 }}>
                              {a.syncError}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Button size="small" onClick={() => setEdit(a)}>
                            Bearbeiten
                          </Button>
                          <Button size="small" onClick={() => toggle.mutate(a)} disabled={toggle.isPending}>
                            {a.active ? 'Deaktivieren' : 'Aktivieren'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </QueryBoundary>
      </Card>
      {edit && <AgentDialog agent={edit === 'new' ? null : edit} onClose={() => setEdit(null)} />}
    </>
  );
}

function AgentDialog({ agent, onClose }: { agent: AgentProfileView | null; onClose: () => void }) {
  const id = useId();
  const qc = useQueryClient();
  const [name, setName] = useState(agent?.displayName ?? '');
  const [signature, setSignature] = useState(agent?.signature ?? '');
  const [gs, setGs] = useState<string[]>(agent?.gesellschaftIds ?? []);
  const save = useMutation({
    mutationFn: () =>
      agent
        ? api.updateAgent(agent.id, { expectedRowVersion: agent.rowVersion, displayName: name.trim(), signature: signature.trim() || null, gesellschaftIds: gs })
        : api.createAgent({ displayName: name.trim(), signature: signature.trim() || null, gesellschaftIds: gs }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.agents });
      onClose();
    },
  });
  const fe = save.error instanceof ApiError ? save.error.fieldErrors : {};
  return (
    <Dialog open onClose={onClose} aria-labelledby={id} maxWidth="sm" fullWidth>
      <DialogTitle id={id}>{agent ? 'Bearbeiterprofil bearbeiten' : 'Bearbeiterprofil anlegen'}</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2.5 }}>
        <TextField label="Anzeigename" value={name} onChange={(e) => setName(e.target.value)} required error={!!fe.displayName} helperText={fe.displayName ?? 'Wird in Nidus als Bearbeiter angezeigt.'} autoFocus />
        <TextField
          label="Signatur (optional)"
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          multiline
          minRows={2}
          error={!!fe.signature}
          helperText={fe.signature ?? 'Nur Grußformel, Name, Kontakt – keine fachlichen Zusagen.'}
        />
        <GesellschaftSelect value={gs} onChange={setGs} error={fe.gesellschaftIds} helperText="Für welche Gesellschaften darf dieses Profil antworten?" />
        {save.error && !Object.keys(fe).length ? <ErrorAlert error={save.error} action="Profil speichern" /> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={name.trim().length < 2 || gs.length === 0 || save.isPending}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}
